import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { FormControlLabel, Switch } from "@mui/material";
import { setSelectedPlan } from "../redux/slices/subscriptionSlice";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { PROD_URL } from "assets/constants";
import { updateCallSettings } from "service/axiosConfig";
// imports you likely already have
import { Alert, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { trackPurchaseIntent } from "utils/analytics";
// ✅ Plan mappings
const planStringToKey = {
  //Trial: "free",
  Wellcheck: "wellcheck",
  Plus: "plus",
  Premium: "premium",
};

const planKeyToEnum = {
  //free: 4,
  wellcheck: 1,
  plus: 2,
  premium: 3,
};

// ✅ All Plans
const plans = [
  // {
  //   key: "free",
  //   title: "Free Trial",
  //   price: "First Week Free",
  //   features: [
  //     "3 free wellchecks",
  //     "Health Summaries and Trends",
  //     "Get started with peace of mind",
  //   ],
  // },
  {
    key: "wellcheck",
    title: "Heartline Wellcheck",
    price: "$17.99/month",
    features: [
      "3 wellchecks per week",
      "Health Summaries and Trends",
      "Emergent Issue Alerts",
      "First Week Free",
    ],
  },
  {
    key: "plus",
    title: "Heartline Family Plus",
    price: "$19.99/month",
    features: [
      "3 wellchecks per week",
      "Custom wellcheck topics",
      "Health Summaries and Trends",
      "Emergent Issue Alerts",
    ],
  },
  {
    key: "premium",
    title: "Heartline Premium",
    price: "$39.99/month",
    features: [
      "Daily wellchecks",
      "Custom wellcheck topics",
      "Health Summaries and Trends",
      "Emergent Issue Alerts",
    ],
  },
];

export default function SubscriptionPlans() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        // Browser restored page from bfcache — force full reload
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [showUnsubscribeOptions, setShowUnsubscribeOptions] = useState(false);
  const [unsubscribeChoice, setUnsubscribeChoice] = useState(""); // "endOfCycle" or "immediate"

  const dispatch = useDispatch();
  const selectedPlan = useSelector((state) => state.subscription.selectedPlan);
  const location = useLocation();
  const [planEndDate, setPlanEndDate] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentPlanStatus, setCurrentPlanStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showRenewDialog, setShowRenewDialog] = useState(false);
  const [showResubDialog, setShowResubDialog] = useState(false);
  const [currentPlanMessage, setCurrentPlanMessage] = useState(null);
  const preSelectedPlan = localStorage.getItem("preSelectedPlan");

  // ✅ Load user's current plan from backend
  const fetchCurrentPlan = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(`${PROD_URL}Auth/GetUserCurrentPlane`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const backendPlanString = response.data.data.plan;
      const endDate = response?.data?.data?.planExpiredAt;
      const planStatus = response?.data?.data?.status;
      setCurrentPlanStatus(planStatus);
      localStorage.setItem("planStatus", planStatus);
      setPlanEndDate(endDate);
      const frontendKey = planStringToKey[backendPlanString];
      setCurrentPlan(frontendKey);
      if (frontendKey) {
        console.log("Key: " + frontendKey);
        if (frontendKey !== "free") {
          localStorage.removeItem("_s43");
        }
        await localStorage.setItem("verifiedUserPlan", planKeyToEnum[frontendKey]);
        dispatch(setSelectedPlan(frontendKey));
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error("Failed to load current plan", err);
    }
  };

  useEffect(() => {
    fetchCurrentPlan();
  }, []);

  useEffect(() => {
    if (location?.state?.fromWordPress && preSelectedPlan) {
      dispatch(setSelectedPlan(preSelectedPlan));
      setShowModal(true);
    }
  }, [location, preSelectedPlan, dispatch]);

  // ✅ Handle checkout redirect from modal
  const handleContinueToCheckout = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const subscriptionPlanId = planKeyToEnum[preSelectedPlan];
      const userId = parseInt(localStorage.getItem("userId"), 10);

      const response = await toast.promise(
        axios.post(
          `${PROD_URL}payment/checkout?userId=${userId}&plan=${subscriptionPlanId}`,
          null,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        {
          pending: "Redirecting to Stripe...",
          success: "Checkout session created!",
          error: "Failed to start checkout.",
        }
      );

      localStorage.removeItem("preSelectedPlan");
      // 🛒 Fire purchase intent GA event
      trackPurchaseIntent(plan.title, plan.price);

      // 💾 Use GA-specific keys for clarity
      localStorage.setItem("GA_SelectedPlanName", plan.title);
      localStorage.setItem("GA_SelectedPlanPrice", plan.price.replace(/[^0-9.]/g, ""));
      window.location.href = response?.data?.checkoutUrl;
    } catch (err) {
      console.error("Stripe checkout failed:", err);
    }
  };

  const disabledTrial = ["wellcheck", "plus", "premium"].includes(selectedPlan);
  // Helper: returns the CTA label for a given target plan
  const getPlanActionLabel = (currentKey, targetKey, currentPlanStatus) => {
    // Same plan selected
    if (currentKey === targetKey) {
      if (currentPlanStatus === "Canceled" && targetKey !== "free") return "Resubscribe";
      if (currentPlanStatus === "Expired" && targetKey !== "free") return "Renew Plan";
      if (currentPlanStatus === "Expired" && targetKey === "free") return "Trial Completed";
      return "Current Plan";
    }

    // Different plan selected
    if (isDowngrade(currentKey, targetKey)) return "Downgrade";
    if (isUpgrade(currentKey, targetKey)) return "Upgrade Plan";

    // Fallback (shouldn't hit if isDowngrade/isUpgrade cover all non-equal cases)
    return "Select Plan";
  };

  async function handleUnsubscribe(type) {
    try {
      const token = localStorage.getItem("accessToken");
      const userId = parseInt(localStorage.getItem("userId"), 10);
      // Convert type to boolean
      const immediate = type === "immediate";

      const response = await toast.promise(
        axios.post(`${PROD_URL}subscription/cancel?userId=${userId}&immediate=${immediate}`, null, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        {
          pending: "Processing unsubscribe...",
          success: "✅ You’ve been unsubscribed.",
          error: "❌ Failed to unsubscribe.",
        }
      );

      // ✅ Only dispatch after successful API call
      if (response?.status === 200) {
        setCurrentPlanStatus(immediate ? "Expired" : "Canceled");
      }

      setShowUnsubscribeOptions(false);
    } catch (err) {
      console.error("Unsubscribe failed:", err);
    }
  }
  useEffect(() => {
    const today = new Date();
    const end = planEndDate ? new Date(planEndDate) : null;

    if (currentPlanStatus === "Expired") {
      setCurrentPlanMessage("Please select a plan to re-activate your account.");
      return;
    }

    if (currentPlanStatus === "Canceled" && end && end > today) {
      setCurrentPlanMessage(
        `Your current plan will expire on ${end.toLocaleDateString()}. You can re-activate at any time.`
      );
      return;
    }

    if (currentPlanStatus === "Active" && end) {
      setCurrentPlanMessage(`Your current plan renews on ${end.toLocaleDateString()}.`);
      return;
    }

    if (currentPlanStatus === "Trial" && end) {
      setCurrentPlanMessage(
        `Your current trial will expire on ${end.toLocaleDateString()}. Upgrade Plan!`
      );
      return;
    }

    setCurrentPlanMessage("No plan information available.");
  }, [currentPlanStatus, planEndDate]);

  const handleRenew = async () => {
    const token = localStorage.getItem("accessToken");
    const subscriptionPlanId = planKeyToEnum[currentPlan];
    const userId = parseInt(localStorage.getItem("userId"), 10);
    try {
      const response = await toast.promise(
        axios.post(
          `${PROD_URL}subscription/renew?userId=${userId}&plan=${subscriptionPlanId}`,
          null,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        {
          pending: "Renewing your plan...",
          success: "Plan Renewed Successfully!",
          error: "Failed to renew plan. Please contact support.",
        }
      );
      if (response?.status === 200) {
        setCurrentPlanStatus("Active");
      }
      dispatch(setSelectedPlan(plan.key));
    } catch (err) {
      console.error("Checkout failed:", err);
    }
  };

  const handleResub = async () => {
    const token = localStorage.getItem("accessToken");
    const subscriptionPlanId = planKeyToEnum[currentPlan];
    const userId = parseInt(localStorage.getItem("userId"), 10);
    try {
      var response = await toast.promise(
        axios.post(`${PROD_URL}subscription/resubscribe?userId=${userId}`, null, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        {
          pending: "Resubscribing your plan...",
          success: "You have been resubscribed!",
          error: "Failed to resubscribe.",
        }
      );
      if (response?.status === 200) {
        setCurrentPlanStatus("Active");
      }
      dispatch(setSelectedPlan(plan.key));
    } catch (err) {
      console.error("Resubscribe failed:", err);
    }
  };
  // put near your plan maps
  const isDowngrade = (currentKey, targetKey) => {
    if (!currentKey || currentKey === targetKey) return false;
    if (targetKey === "free") return true; // anything → free is a downgrade
    if (currentKey === "free") return false; // free → anything paid is upgrade
    return planKeyToEnum[targetKey] < planKeyToEnum[currentKey]; // smaller enum = lower plan (excl. free)
  };

  const handleDowngrade = async (targetPlanKey) => {
    const token = localStorage.getItem("accessToken");
    const userId = parseInt(localStorage.getItem("userId") || "0", 10);
    if (!userId || !token) {
      toast.error("Not authenticated.");
      return null;
    }

    const reqBody = {
      userId,
      targetPlan: planKeyToEnum[targetPlanKey], // backend enum value
      when: 2, // EndOfCycle
    };

    try {
      const request = axios.post(`${PROD_URL}subscription/downgrade`, reqBody, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const res = await toast.promise(request, {
        pending: "Scheduling downgrade…",
        success: {
          render({ data }) {
            const { bannerText } = (data && data.data) || {};
            return bannerText || "Downgrade scheduled.";
          },
        },
        error: {
          render({ data }) {
            const msg =
              data?.response?.data?.message || data?.message || "Failed to schedule downgrade.";
            return msg;
          },
        },
      });

      const { bannerText, effectiveDate, pending } = res?.data || {};
      // Optional: use these in UI
      // setDowngradeBanner({ text: bannerText, effectiveDate });
      return { bannerText, effectiveDate, pending };
    } catch (err) {
      // toast.promise already showed the error toast
      console.error("Downgrade failed:", err);
      return null;
    }
  };

  const PLAN_LABELS = {
    premium: "Premium",
    plus: "Family",
    wellcheck: "Individual Wellcheck",
    free: "Free",
  };

  const FEATURE_MATRIX = {
    "premium->plus": [
      ["Max Frequency", true, false],
      ["Up to 5 caregivers", true, true],
      ["Custom Topic selection", true, true],
      ["SMS alerts", true, true],
      ["Email Summary", true, true],
      ["Online Health History", true, true],
      ["Trends and Doctor Reports", true, true],
    ],
    "premium->wellcheck": [
      ["Max Frequency", true, false],
      ["Caregivers", true, false],
      ["Custom Topic selection", true, false],
      ["SMS alerts", true, true],
      ["Email Summary", true, true],
      ["Online Health History", true, true],
      ["Trends and Doctor Reports", true, true],
    ],
    "plus->wellcheck": [
      ["Max Frequency", false, false],
      ["Caregivers", true, false],
      ["Custom Topic selection", true, false],
      ["SMS alerts", true, true],
      ["Email Summary", true, true],
      ["Online Health History", true, true],
      ["Trends and Doctor Reports", true, true],
    ],
  };

  const SCENARIO_NOTE = {
    "premium->plus":
      "This downgrade will default your wellcheck days to Mon–Wed–Fri. Please update your call settings as appropriate.",
    "premium->wellcheck":
      "This downgrade will default your wellcheck days to Mon–Wed–Fri. Please update your call settings as appropriate.",
    "plus->wellcheck":
      "This downgrade will default your wellcheck days to Mon–Wed–Fri. Please update your call settings as appropriate.",
  };
  const isUpgrade = (currentKey, targetKey) => {
    if (!currentKey || currentKey === targetKey) return false;
    if (targetKey === "free") return false; // never upgrade to free
    if (currentKey === "free") return false; // free → paid is upgrade
    return planKeyToEnum[targetKey] > planKeyToEnum[currentKey]; // bigger enum = higher plan
  };
  const UPGRADE_FEATURE_MATRIX = {
    "free->plus": [
      ["Max Frequency", false, true],
      ["Up to 5 caregivers", false, true],
      ["Custom Topic selection", false, true],
      ["SMS alerts", false, true],
      ["Email Summary", false, true],
      ["Online Health History", false, true],
      ["Trends and Doctor Reports", false, true],
    ],
    "free->premium": [
      ["Max Frequency", false, true],
      ["Up to 5 caregivers", false, true],
      ["Custom Topic selection", false, true],
      ["SMS alerts", false, true],
      ["Email Summary", false, true],
      ["Online Health History", false, true],
      ["Trends and Doctor Reports", false, true],
    ],
    "plus->premium": [
      ["Max Frequency", false, true],
      ["All caregivers", true, true],
      ["Custom Topic selection", true, true],
      ["SMS alerts", true, true],
      ["Email Summary", true, true],
      ["Online Health History", true, true],
      ["Trends and Doctor Reports", true, true],
    ],
    "wellcheck->plus": [
      ["Max Frequency", false, false],
      ["Up to 5 caregivers", false, true],
      ["Custom Topic selection", false, true],
      ["SMS alerts", true, true],
      ["Email Summary", true, true],
      ["Online Health History", true, true],
      ["Trends and Doctor Reports", true, true],
    ],
    "wellcheck->premium": [
      ["Max Frequency", false, true],
      ["All caregivers", false, true],
      ["Custom Topic selection", false, true],
      ["SMS alerts", true, true],
      ["Email Summary", true, true],
      ["Online Health History", true, true],
      ["Trends and Doctor Reports", true, true],
    ],
  };
  const UPGRADE_NOTE = {
    "free->plus":
      "Upgrading unlocks more caregiver slots and email summaries to keep your family in sync.",
    "free->premium":
      "Premium gives you maximum frequency, unlimited caregivers, and all advanced reporting features.",
    "plus->premium": "Premium unlocks higher frequency wellchecks and full caregiver flexibility.",
    "wellcheck->plus":
      "Family adds caregiver slots, custom topics, and shared management features.",
    "wellcheck->premium":
      "Premium upgrades you to maximum frequency, unlimited caregivers upto 5, and all advanced reports.",
  };

  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeFrom, setUpgradeFrom] = useState(null);
  const [upgradeTo, setUpgradeTo] = useState(null);
  const [upgradeBanner, setUpgradeBanner] = useState(null);

  const scenarioKey = (fromKey, toKey) => `${fromKey}->${toKey}`;
  // state
  const [showDowngradeDialog, setShowDowngradeDialog] = useState(false);
  const [downgradeFrom, setDowngradeFrom] = useState(null);
  const [downgradeTo, setDowngradeTo] = useState(null);
  const [downgradeBanner, setDowngradeBanner] = useState(null);
  const handleUpgrade = async (targetPlanKey) => {
    const token = localStorage.getItem("accessToken");
    const userId = parseInt(localStorage.getItem("userId") || "0", 10);
    if (!userId || !token) {
      toast.error("Not authenticated.");
      return null;
    }

    const reqBody = {
      userId,
      targetPlan: planKeyToEnum[targetPlanKey], // backend enum value
      when: 1, // Immediate
    };

    try {
      const request = axios.post(`${PROD_URL}subscription/upgrade`, reqBody, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const res = await toast.promise(request, {
        pending: "Upgrading your plan…",
        success: {
          render({ data }) {
            const { bannerText } = (data && data.data) || {};
            return bannerText || "Plan has been upgraded.";
          },
        },
        error: {
          render({ data }) {
            const msg =
              data?.response?.data?.message || data?.message || "Failed to schedule downgrade.";
            return msg;
          },
        },
      });

      const { bannerText, effectiveDate, pending } = res?.data || {};
      // Optional: use these in UI
      // setDowngradeBanner({ text: bannerText, effectiveDate });
      return { bannerText, effectiveDate, pending };
    } catch (err) {
      // toast.promise already showed the error toast
      console.error("Downgrade failed:", err);
      return null;
    }
  };
  return (
    <>
      {/* Burgundy Header */}
      <header
        style={{
          backgroundColor: "#561919",
          color: "#fff",
          padding: "1rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
        <div>
          <Link
            to="https://myheartline.com/"
            style={{
              fontWeight: "bold",
              fontSize: "1.25rem",
              color: "#fff",
              textDecoration: "underline",
            }}
          >
            Heartline
          </Link>
        </div>
        {/* <nav style={{ display: "flex", gap: "2rem" }}>
          <Link
            to="https://myheartline.com/contact/"
            style={{ textDecoration: "none", color: "#fff", fontWeight: "bold" }}
          >
            Contact
          </Link>
          <Link
            to="/authentication/demo"
            style={{ textDecoration: "none", color: "#fff", fontWeight: "bold" }}
          >
            Demo
          </Link>
        </nav> */}
      </header>
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  bgColor="rgb(86,25,25)"
                  borderRadius="lg"
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    Subscription Plan
                  </MDTypography>
                  <DashboardNavbar />
                </MDBox>
                {loading ? (
                  <MDBox display="flex" alignItems="center" justifyContent="center" height="200px">
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <MDBox>
                    {/* Dissmissal Banner
                    {downgradeBanner && (
                      <Alert
                        severity="info"
                        sx={{ mb: 2, alignItems: "center" }}
                        action={
                          <IconButton
                            color="inherit"
                            size="small"
                            onClick={() => setDowngradeBanner(null)}
                          >
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        }
                      >
                        {downgradeBanner.text}
                        {downgradeBanner.effectiveDate
                          ? ` Changes take effect on ${downgradeBanner.effectiveDate}.`
                          : ""}
                      </Alert>
                    )} */}
                    <MDBox pt={3} px={2} pb={3}>
                      <Typography variant="body2" sx={{ mt: 2, fontWeight: 500, color: "#333" }}>
                        {currentPlanMessage}
                      </Typography>
                    </MDBox>

                    <MDBox pt={3} px={2} pb={3}>
                      <Grid container spacing={4}>
                        {plans.map((plan) => (
                          <Grid item xs={12} sm={6} md={4} lg={4} key={plan.key}>
                            <Card
                              sx={{
                                border:
                                  selectedPlan === plan.key
                                    ? "2px solid #561919"
                                    : "1px solid #ccc",
                                backgroundColor: selectedPlan === plan.key ? "#fdf3f3" : "#fff",
                                height: "100%",
                                boxShadow:
                                  selectedPlan === plan.key
                                    ? "0 4px 20px rgba(86, 25, 25, 0.2)"
                                    : "none",
                              }}
                            >
                              <CardContent
                                sx={{ display: "flex", flexDirection: "column", height: "100%" }}
                              >
                                <Box flexGrow={1}>
                                  <Typography variant="h6" mb={1}>
                                    {plan.title}
                                  </Typography>
                                  <Typography variant="subtitle1" mb={2}>
                                    {plan.price}
                                  </Typography>
                                  {plan.features.map((feature, idx) => (
                                    <Typography key={idx} variant="body2" color="text.secondary">
                                      + {feature}
                                    </Typography>
                                  ))}
                                </Box>
                                <MDBox mt={2}>
                                  <Button
                                    fullWidth
                                    disableElevation
                                    disableRipple
                                    disabled={plan.key === "free"}
                                    onClick={async () => {
                                      const token = localStorage.getItem("accessToken");
                                      const subscriptionPlanId = planKeyToEnum[plan.key];
                                      const userId = parseInt(localStorage.getItem("userId"), 10);

                                      // If Coming Soon, redirect as before
                                      // if (plan.key === "plus") {
                                      //   window.location.href =
                                      //     "https://myheartline.com/heartline-plus-coming-soon/";
                                      //   return;
                                      // }
                                      // if (plan.key === "premium") {
                                      //   window.location.href =
                                      //     "https://myheartline.com/heartline-premium-coming-soon/";
                                      //   return;
                                      // }
                                      // onClick for downgrade branch
                                      if (isDowngrade(selectedPlan, plan.key)) {
                                        setDowngradeFrom(selectedPlan);
                                        setDowngradeTo(plan.key);
                                        setShowDowngradeDialog(true);
                                        return;
                                      }
                                      if (isUpgrade(selectedPlan, plan.key)) {
                                        setUpgradeFrom(selectedPlan);
                                        setUpgradeTo(plan.key);
                                        setShowUpgradeDialog(true);
                                        return;
                                      }

                                      // Handle canceled plan with non-trial selection
                                      if (
                                        currentPlanStatus === "Canceled" &&
                                        currentPlan !== "free"
                                      ) {
                                        setShowResubDialog(true);
                                        return;
                                      }

                                      // Handle expired plan: always allow checkout
                                      if (
                                        currentPlanStatus === "Expired" &&
                                        currentPlan !== "free"
                                      ) {
                                        setShowRenewDialog(true);
                                        return;
                                      }

                                      // Normal case: avoid duplicate selection
                                      if (selectedPlan === plan.key) return;

                                      // Normal checkout flow
                                      try {
                                        const response = await toast.promise(
                                          axios.post(
                                            `${PROD_URL}payment/checkout?userId=${userId}&plan=${subscriptionPlanId}`,
                                            null,
                                            { headers: { Authorization: `Bearer ${token}` } }
                                          ),
                                          {
                                            pending: "Redirecting to Stripe...",
                                            success: "Checkout session created!",
                                            error: "Failed to start checkout.",
                                          }
                                        );
                                        // 🛒 Fire purchase intent GA event
                                        trackPurchaseIntent(plan.title, plan.price);

                                        // 💾 Use GA-specific keys for clarity
                                        localStorage.setItem("GA_SelectedPlanName", plan.title);
                                        localStorage.setItem(
                                          "GA_SelectedPlanPrice",
                                          plan.price.replace(/[^0-9.]/g, "")
                                        );
                                        window.location.href = response?.data?.checkoutUrl;
                                        dispatch(setSelectedPlan(plan.key));
                                      } catch (err) {
                                        console.error("Checkout failed:", err);
                                      }
                                    }}
                                    sx={{
                                      backgroundColor:
                                        selectedPlan === plan.key ? "#561919" : "#fff",
                                      color: selectedPlan === plan.key ? "#fff" : "#561919",
                                      border: "2px solid #561919",
                                      fontWeight: "bold",
                                      textTransform: "uppercase",
                                      padding: "12px",
                                      "&:hover": {
                                        backgroundColor:
                                          selectedPlan === plan.key ? "#6A1B1B" : "#f5f5f5",
                                        color: selectedPlan === plan.key ? "#fff" : "#561919",
                                      },
                                      "&:disabled": {
                                        boxShadow: "none",
                                        color:
                                          selectedPlan === plan.key
                                            ? "#fff !important"
                                            : "#561919 !important",
                                      },
                                      "& *": {
                                        color:
                                          selectedPlan === plan.key
                                            ? "#fff !important"
                                            : "#561919 !important",
                                      },
                                    }}
                                  >
                                    {getPlanActionLabel(selectedPlan, plan.key, currentPlanStatus)}
                                  </Button>
                                </MDBox>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </MDBox>

                    {currentPlanStatus !== "Expired" && (
                      <MDBox
                        mt={2}
                        pt={3}
                        px={2}
                        pb={3}
                        display="flex"
                        justifyContent="flex-end"
                        width="100%"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            cursor: "pointer",
                            color: "#561919",
                            fontWeight: "bold",
                            textDecoration: "underline",
                          }}
                          onClick={() => setShowUnsubscribeOptions(true)}
                        >
                          Manage Subscription
                        </Typography>
                      </MDBox>
                    )}
                  </MDBox>
                )}
              </Card>
            </Grid>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
      {/* Unsubscribe Options Modal */}
      {/* This modal is shown when the user clicks "Unsubscribe" on the on plan screen button */}
      <Dialog
        open={showUnsubscribeOptions}
        onClose={() => setShowUnsubscribeOptions(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.25rem",
            color: "#561919",
          }}
        >
          Unsubscribe from Heartline
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" align="center" sx={{ color: "#561919", mb: 2 }}>
            Your health check-ins and alerts will end after the billing period
            <strong>
              {" "}
              {planEndDate && (
                <>
                  {" "}
                  on <strong>{new Date(planEndDate).toLocaleDateString()}</strong>.
                </>
              )}
            </strong>
            .
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{
              color: "#561919",
              fontWeight: "bold",
              mb: 3,
            }}
          >
            Stop calls immediately?
          </Typography>
          <Box display="flex" gap={2} justifyContent="center">
            {/* Stop Calls Immediately */}
            <Button
              fullWidth
              onClick={() => handleUnsubscribe("immediate")}
              sx={{
                backgroundColor: "#561919",
                color: "#fff",
                border: "2px solid #561919",
                fontWeight: "bold",
                textTransform: "uppercase",
                padding: "12px",
                "&:hover": {
                  backgroundColor: "#6A1B1B",
                  color: "#fff",
                },
              }}
            >
              Stop Calls
            </Button>

            {/* Continue Calls Until End */}
            <Button
              fullWidth
              onClick={() => handleUnsubscribe("endOfCycle")}
              sx={{
                backgroundColor: "#fff",
                color: "#561919",
                border: "2px solid #561919",
                fontWeight: "bold",
                textTransform: "uppercase",
                padding: "12px",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  color: "#561919",
                },
              }}
            >
              Continue Calls
            </Button>
          </Box>

          {/* Cancel */}
          <Button
            fullWidth
            onClick={() => setShowUnsubscribeOptions(false)}
            sx={{
              mt: 2,
              color: "#555",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#555",
              },
            }}
          >
            Cancel
          </Button>
        </DialogContent>
      </Dialog>
      {/* Renew Options Modal */}
      {/* This modal is shown when the user clicks "Renew" on the on plan screen button */}
      <Dialog open={showRenewDialog} onClose={() => setShowRenewDialog(false)}>
        <DialogTitle sx={{ fontWeight: "bold", color: "#561919" }}>
          Reactivate Subscription
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>Your current plan has expired.</Typography>
          <Typography>Would you like to re-activate this subscription?</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Button
            fullWidth
            onClick={() => setShowRenewDialog(false)}
            sx={{
              backgroundColor: "#fff",
              color: "#561919",
              border: "2px solid #561919",
              fontWeight: "bold",
              textTransform: "uppercase",
              padding: "12px",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                color: "#561919",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            fullWidth
            onClick={() => {
              setShowRenewDialog(false);
              handleRenew();
            }}
            sx={{
              backgroundColor: "#561919",
              color: "#fff",
              border: "2px solid #561919",
              fontWeight: "bold",
              textTransform: "uppercase",
              padding: "12px",
              "&:hover": {
                backgroundColor: "#6A1B1B",
                color: "#fff",
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      {/* Resub Options Modal */}
      {/* This modal is shown when the user clicks "Resubscribe" on the on plan screen button */}
      <Dialog open={showResubDialog} onClose={() => setShowResubDialog(false)}>
        <DialogTitle sx={{ fontWeight: "bold", color: "#561919" }}>
          Reactivate Subscription
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Your current plan will expired
            <strong>
              {" "}
              {planEndDate && (
                <>
                  {" "}
                  on <strong>{new Date(planEndDate).toLocaleDateString()}</strong>.
                </>
              )}
            </strong>
            .
          </Typography>
          <Typography>Would you like to re-activate and continue your subscription?</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Button
            fullWidth
            onClick={() => setShowResubDialog(false)}
            sx={{
              backgroundColor: "#fff",
              color: "#561919",
              border: "2px solid #561919",
              fontWeight: "bold",
              textTransform: "uppercase",
              padding: "12px",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                color: "#561919",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            fullWidth
            onClick={() => {
              setShowResubDialog(false);
              handleResub();
            }}
            sx={{
              backgroundColor: "#561919",
              color: "#fff",
              border: "2px solid #561919",
              fontWeight: "bold",
              textTransform: "uppercase",
              padding: "12px",
              "&:hover": {
                backgroundColor: "#6A1B1B",
                color: "#fff",
              },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      {/*Downgrade Confirm Modal */}
      <Dialog
        open={showDowngradeDialog}
        onClose={() => setShowDowngradeDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#561919" }}>
          Confirm your downgrade
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Your plan will expire on <strong>the end of your current billing cycle</strong>. Here’s
            a summary of what’s changing:
          </Typography>

          {downgradeFrom && downgradeTo && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {PLAN_LABELS[downgradeFrom] || downgradeFrom}
                    </Typography>
                    {(FEATURE_MATRIX[scenarioKey(downgradeFrom, downgradeTo)] || []).map(
                      ([label, fromHas], i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                          {fromHas ? (
                            <CheckCircleOutlineIcon sx={{ color: "green" }} />
                          ) : (
                            <HighlightOffIcon sx={{ color: "red" }} />
                          )}
                          <Typography sx={{ ml: 1 }}>{label}</Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {PLAN_LABELS[downgradeTo] || downgradeTo}
                    </Typography>
                    {(FEATURE_MATRIX[scenarioKey(downgradeFrom, downgradeTo)] || []).map(
                      ([label, , toHas], i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                          {toHas ? (
                            <CheckCircleOutlineIcon sx={{ color: "green" }} />
                          ) : (
                            <HighlightOffIcon sx={{ color: "red" }} />
                          )}

                          <Typography sx={{ ml: 1 }}>{label}</Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </Grid>
              </Grid>

              {SCENARIO_NOTE[scenarioKey(downgradeFrom, downgradeTo)] && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  {SCENARIO_NOTE[scenarioKey(downgradeFrom, downgradeTo)]}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Button
            fullWidth
            onClick={() => setShowDowngradeDialog(false)}
            sx={{
              backgroundColor: "#fff",
              color: "#561919",
              border: "2px solid #561919",
              fontWeight: "bold",
              textTransform: "uppercase",
              p: "12px",
              "&:hover": { backgroundColor: "#f5f5f5", color: "#561919" },
            }}
          >
            Cancel and keep current plan
          </Button>

          <Button
            fullWidth
            onClick={async () => {
              if (!downgradeTo) return;
              const result = await handleDowngrade(downgradeTo);
              setShowDowngradeDialog(false);
              if (result?.bannerText) {
                setDowngradeBanner({
                  text: result.bannerText,
                  effectiveDate: result.effectiveDate,
                });
              }
            }}
            sx={{
              backgroundColor: "#561919",
              color: "#fff",
              border: "2px solid #561919",
              fontWeight: "bold",
              textTransform: "uppercase",
              p: "12px",
              "&:hover": { backgroundColor: "#6A1B1B", color: "#fff" },
            }}
          >
            Confirm Downgrade
          </Button>
        </DialogActions>
      </Dialog>
      {/* Upgrade Confirm Modal */}
      <Dialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#195619" }}>
          Confirm your upgrade
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Your plan will be upgraded <strong>immediately</strong>. Here’s a summary of what’s new:
          </Typography>

          {upgradeFrom && upgradeTo && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {PLAN_LABELS[upgradeFrom] || upgradeFrom}
                    </Typography>
                    {(UPGRADE_FEATURE_MATRIX[scenarioKey(upgradeFrom, upgradeTo)] || []).map(
                      ([label, fromHas], i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                          {fromHas ? (
                            <CheckCircleOutlineIcon sx={{ color: "green" }} />
                          ) : (
                            <HighlightOffIcon sx={{ color: "red" }} />
                          )}
                          <Typography sx={{ ml: 1 }}>{label}</Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {PLAN_LABELS[upgradeTo] || upgradeTo}
                    </Typography>
                    {(UPGRADE_FEATURE_MATRIX[scenarioKey(upgradeFrom, upgradeTo)] || []).map(
                      ([label, , toHas], i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                          {toHas ? (
                            <CheckCircleOutlineIcon sx={{ color: "green" }} />
                          ) : (
                            <HighlightOffIcon sx={{ color: "red" }} />
                          )}
                          <Typography sx={{ ml: 1 }}>{label}</Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </Grid>
              </Grid>

              {UPGRADE_NOTE[scenarioKey(upgradeFrom, upgradeTo)] && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {UPGRADE_NOTE[scenarioKey(upgradeFrom, upgradeTo)]}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Button
            fullWidth
            onClick={() => setShowUpgradeDialog(false)}
            sx={{
              backgroundColor: "#fff",
              color: "#195619",
              border: "2px solid #195619",
              fontWeight: "bold",
              textTransform: "uppercase",
              p: "12px",
              "&:hover": { backgroundColor: "#f5f5f5", color: "#195619" },
            }}
          >
            Cancel
          </Button>

          <Button
            fullWidth
            onClick={async () => {
              if (!upgradeTo) return;
              const result = await handleUpgrade(upgradeTo);
              setShowUpgradeDialog(false);
              if (result?.bannerText) {
                setUpgradeBanner({
                  text: result.bannerText,
                  effectiveDate: result.effectiveDate,
                });
              }
            }}
            sx={{
              backgroundColor: "#195619",
              color: "#fff",
              border: "2px solid #195619",
              fontWeight: "bold",
              textTransform: "uppercase",
              p: "12px",
              "&:hover": { backgroundColor: "#2E7D32", color: "#fff" },
            }}
          >
            Confirm Upgrade
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Modal code */}
      <Dialog open={showModal} onClose={() => setShowModal(false)}>
        <DialogTitle sx={{ fontWeight: "bold", color: "#561919" }}>
          Subscription Plan Selected
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            You selected <b>{plans.find((p) => p.key === preSelectedPlan)?.title}</b> plan.
          </Typography>
          <Typography>Would you like to proceed to checkout?</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              localStorage.removeItem("preSelectedPlan");
              dispatch(setSelectedPlan(null));
              setShowModal(false);
            }}
            sx={{
              borderColor: "#561919",
              color: "#561919",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#fdf3f3",
                borderColor: "#561919",
              },
              "&:focus": {
                backgroundColor: "#fdf3f3",
                borderColor: "#561919",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleContinueToCheckout}
            sx={{
              backgroundColor: "#561919",
              color: "#fff",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#6A1B1B",
              },
              "&:focus": {
                backgroundColor: "#6A1B1B",
              },
            }}
          >
            Continue to Checkout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
